import { useState, useEffect, useRef } from 'react';
import { Villa, STATUS_LABELS, STATUSES_ORDER, DISTRICTS } from '../types';
import {
  Sheet,
  SheetContent,
} from './ui/sheet';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { ExternalLink, Archive, X, Images, Trash2, Check, Star, Calendar } from 'lucide-react';
import { updateVilla } from '../api';

interface VillaDrawerProps {
  villa: Villa | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (villa: Villa) => void;
  onMoveToArchive: (id: string) => void;
}

export default function VillaDrawer({
  villa,
  open,
  onOpenChange,
  onChange,
  onMoveToArchive,
}: VillaDrawerProps) {
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<Villa['status']>('new');
  const [district, setDistrict] = useState<string>('');
  const [price, setPrice] = useState('');
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesDialogOpen, setImagesDialogOpen] = useState(false);
  const [editingImages, setEditingImages] = useState<string[]>([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const mouseDownX = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 768);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const displayImages = villa?.images && villa.images.length > 0
    ? villa.images
    : (villa?.image ? [villa.image] : []);

  const totalImages = displayImages.length;

  useEffect(() => {
    if (villa) {
      setComment(villa.comment || '');
      setStatus(villa.status);
      setDistrict(villa.district || '');
      setPrice(villa.price ? String(villa.price) : '');
      setCurrentImageIndex(0);
      setEditingImages(villa.images || (villa.image ? [villa.image] : []));
      setCoverIndex(0);
    }
  }, [villa]);

  const handleSaveComment = async () => {
    if (!villa) return;
    try {
      const updated = await updateVilla(villa.id, { comment });
      onChange(updated);
    } catch (err) {
      console.error('Failed to save comment:', err);
    }
  };

  const handlePriceBlur = async () => {
    if (!villa) return;
    const newPrice = price.trim() ? parseFloat(price.trim().replace(',', '.')) : null;
    try {
      const updated = await updateVilla(villa.id, { price: newPrice });
      onChange(updated);
    } catch (err) {
      console.error('Failed to save price:', err);
    }
  };

  const handleDistrictChange = async (newDistrict: string) => {
    if (!villa) return;
    const valueToSave = newDistrict === 'none' ? null : newDistrict;
    setDistrict(newDistrict === 'none' ? '' : newDistrict);
    try {
      const updated = await updateVilla(villa.id, { district: valueToSave });
      onChange(updated);
    } catch (err) {
      console.error('Failed to save district:', err);
    }
  };

  const handleStatusChange = async (newStatus: Villa['status']) => {
    if (!villa) return;
    setStatus(newStatus);
    try {
      const updated = await updateVilla(villa.id, { status: newStatus });
      onChange(updated);
    } catch (err) {
      console.error('Failed to save status:', err);
    }
  };

  const handleTogglePin = async () => {
    if (!villa) return;
    try {
      const updated = await updateVilla(villa.id, { pinned: !villa.pinned });
      onChange(updated);
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleWhatsApp = () => {
    if (villa?.phone) {
      window.open(`https://wa.me/${villa.phone.replace(/\D/g, '')}`, '_blank');
    }
  };

  const handlePhoneClick = () => {
    setPhoneInput(villa?.phone || '');
    setPhoneDialogOpen(true);
  };

  const handlePhoneSave = async () => {
    if (!villa) return;
    try {
      const updated = await updateVilla(villa.id, { phone: phoneInput || null });
      onChange(updated);
      setPhoneDialogOpen(false);
      setPhoneInput('');
    } catch (err) {
      console.error('Failed to save phone:', err);
    }
  };

  const openImagesDialog = () => {
    setEditingImages(villa?.images || (villa?.image ? [villa.image] : []));
    if (villa?.image && villa?.images) {
      const idx = villa.images.indexOf(villa.image);
      setCoverIndex(idx >= 0 ? idx : 0);
    }
    setImagesDialogOpen(true);
  };

  const handleDeleteImage = (index: number) => {
    if (editingImages.length <= 1) return;
    const newImages = editingImages.filter((_, i) => i !== index);
    setEditingImages(newImages);
    if (index === coverIndex) {
      setCoverIndex(0);
    } else if (index < coverIndex) {
      setCoverIndex(coverIndex - 1);
    }
  };

  const handleSetCover = (index: number) => {
    setCoverIndex(index);
  };

  const handleSaveImages = async () => {
    if (!villa) return;
    const newImage = editingImages[coverIndex] || null;
    const newImages = editingImages.length > 0 ? editingImages : null;
    try {
      const updated = await updateVilla(villa.id, {
        image: newImage,
        images: newImages,
      });
      onChange(updated);
      setImagesDialogOpen(false);
    } catch (err) {
      console.error('Failed to save images:', err);
    }
  };

  // Свайп для тач-устройств
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        setCurrentImageIndex(currentImageIndex === 0 ? totalImages - 1 : currentImageIndex - 1);
      } else {
        setCurrentImageIndex(currentImageIndex === totalImages - 1 ? 0 : currentImageIndex + 1);
      }
    }
  };

  // Свайп для десктопа (мышь)
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownX.current = e.clientX;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (e.buttons !== 1) return; // Только левая кнопка
    const deltaX = e.clientX - mouseDownX.current;
    if (Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        setCurrentImageIndex(currentImageIndex === 0 ? totalImages - 1 : currentImageIndex - 1);
      } else {
        setCurrentImageIndex(currentImageIndex === totalImages - 1 ? 0 : currentImageIndex + 1);
      }
      mouseDownX.current = e.clientX; // Сброс после переключения
    }
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  const webApp = (window as any).Telegram?.WebApp;

  useEffect(() => {
    const main = webApp?.MainButton;
    if (!main) return;

    main.hide();

    if (!open || !villa) return;
    if (villa.phone) {
      main.setText('Написать в WhatsApp');
      main.onClick(handleWhatsApp);
    } else {
      main.setText('Указать телефон');
      main.onClick(handlePhoneClick);
    }
    main.show();

    return () => {
      main.offClick(handleWhatsApp);
      main.offClick(handlePhoneClick);
      main.hide();
    };
  }, [open, villa?.phone, villa?.id]);

  if (!villa) {
    return null;
  }

  const content = (
    <>
      <button
        onClick={() => onOpenChange(false)}
        className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 md:hidden"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="h-full overflow-y-auto">
        {/* Image Carousel */}
        {displayImages.length > 0 && (
          <div className="relative aspect-video w-full overflow-hidden bg-black">
            {/* Status selector on image */}
            <div className="absolute top-2 left-2 z-10">
              <Select value={status} onValueChange={(v) => handleStatusChange(v as Villa['status'])}>
                <SelectTrigger className="w-32 bg-black/50 backdrop-blur-md text-white border-white/20 text-xs">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES_ORDER.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div
              ref={carouselRef}
              className="flex transition-transform duration-300 ease-out h-full"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
            >
              {displayImages.map((img, i) => (
                <div key={i} className="flex-shrink-0 w-full h-full">
                  <img
                    src={img}
                    alt={`${villa.title || ''} ${i + 1}`}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
              ))}
            </div>

            {/* Old-style dots navigation */}
            {displayImages.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {displayImages.map((_, i) => (
                  <div
                    key={i}
                    onClick={() => goToImage(i)}
                    className={`rounded-full transition-all cursor-pointer ${
                      i === currentImageIndex
                        ? 'bg-white w-8 h-1'
                        : 'bg-white/50 w-2 h-2 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="p-6 space-y-4">
          {/* Title & Link */}
          <div>
            <h2 className="text-xl font-semibold">{villa.title || villa.domain}</h2>
            <div className="flex items-center gap-3 mt-1">
              <a
                href={villa.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground flex items-center gap-1 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                {villa.domain}
              </a>
              {villa.addedAtFormatted && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {villa.addedAtFormatted}
                </span>
              )}
            </div>
          </div>

          {/* Price and District in one row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                type="number"
                step="0.1"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                onBlur={handlePriceBlur}
                placeholder="Цена (млн)"
                className="text-base font-semibold text-green-600 dark:text-green-400"
              />
            </div>
            <div>
              <Select value={district || 'none'} onValueChange={handleDistrictChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Район" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Без района</SelectItem>
                  {DISTRICTS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Comment */}
          <div>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onBlur={handleSaveComment}
              placeholder="Заметки о вилле..."
              rows={3}
            />
          </div>

          {/* Phone Action - always WhatsApp if exists */}
          {villa.phone ? (
            <>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleWhatsApp}
              >
                Открыть WhatsApp ({villa.phone})
              </Button>
              <Button
                variant="ghost"
                className="w-full text-sm"
                onClick={handlePhoneClick}
              >
                Изменить телефон
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              className="w-full"
              onClick={handlePhoneClick}
            >
              Добавить телефон
            </Button>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleTogglePin}
            >
              {villa.pinned ? (
                <>
                  <Star className="h-4 w-4 mr-2 fill-current" />
                  Снять отметку
                </>
              ) : (
                <>
                  <Star className="h-4 w-4 mr-2" />
                  Пометить прикольным
                </>
              )}
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => onMoveToArchive(villa.id)}
            >
              <Archive className="h-4 w-4 mr-2" />
              В архив
            </Button>

            {displayImages.length > 0 && (
              <Button
                variant="outline"
                className="w-full"
                onClick={openImagesDialog}
              >
                <Images className="h-4 w-4 mr-2" />
                Настроить изображения ({displayImages.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Phone Dialog */}
      <Dialog open={phoneDialogOpen} onOpenChange={setPhoneDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{villa.phone ? 'Изменить телефон' : 'Добавить телефон'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handlePhoneSave(); }}>
            <Input
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="+62 812 3456 7890"
            />
            <div className="flex gap-2 justify-end mt-4">
              <Button type="button" variant="outline" onClick={() => setPhoneDialogOpen(false)}>
                Отмена
              </Button>
              <Button type="submit">
                Сохранить
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Images Management Dialog */}
      <Dialog open={imagesDialogOpen} onOpenChange={setImagesDialogOpen}>
        <DialogContent className="max-w-sm max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Изображения ({editingImages.length})</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 overflow-y-auto max-h-[60vh] py-4">
            {editingImages.map((img, i) => (
              <div
                key={i}
                className={`flex gap-3 rounded-lg border-2 transition-colors p-2 ${
                  i === coverIndex ? 'border-green-500 bg-green-500/5' : 'border-border'
                }`}
              >
                <div className="relative flex-shrink-0">
                  <img
                    src={img}
                    alt={`Image ${i + 1}`}
                    className="w-16 h-16 rounded-md object-cover"
                  />
                  {i === coverIndex && (
                    <div className="absolute -top-1 -left-1 bg-green-500 rounded-full p-0.5">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                  )}
                </div>

                <div className="flex-1 flex flex-col justify-between min-w-0">
                  <div className="text-xs text-muted-foreground truncate">
                    Фото {i + 1}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={i === coverIndex ? "default" : "outline"}
                      className="flex-1 h-8 text-xs"
                      onClick={() => handleSetCover(i)}
                    >
                      {i === coverIndex ? 'Обложка' : 'Сделать обложкой'}
                    </Button>
                    {editingImages.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteImage(i)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end border-t pt-4">
            <Button variant="outline" onClick={() => setImagesDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSaveImages}>
              Сохранить
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  return (
    <>
      {isDesktop ? (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-2xl w-full p-0 gap-0 overflow-hidden !border-0">
            {content}
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent className="p-0 border-none">
            {content}
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
